import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  GridOptions,
  ICellRendererParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { intl } from '../../../i18n'
import { columnTypes } from '../../containers/commons/AgGrid'
import { useRowNumber } from '../../page-properties/bulksheet-properties/rowNumber'
import { dragAndDrop } from '../../page-properties/bulksheet-properties/dragAndDrop'
import {
  useRevision,
  useCreatedBy,
  useCreatedAt,
  useUpdatedBy,
  useUpdatedAt,
} from '../../page-properties/bulksheet-properties/changeLog'
import {
  ClientSideTextFilter,
  ClientSideSelectFilter,
} from '../../containers/BulkSheetView/components/filter'
import { toLabel } from '../../../domain/entity/WbsItemEntity'
import { getWbsItemStatusColorCode } from '../../../lib/functions/wbsItem'
import { SubStatusRow } from './SubStatusRow'
import { NameCellRenderer } from './components/NameCellRenderer'
import { LinkedStatusCellRenderer } from './components/LinkedStatusCellRenderer'
import { LinkedStatusCellEditor } from './components/LinkedStatusCellEditor'
import { DEFAULT_CELL_CLASS_RULES } from '../../containers/BulkSheetView/gridOptions/cell/cellClassRules'
import { ERROR_CELL_BACKGROUND } from './lib/gridUtils'

// 紐づくステータスの選択式フィルタ用オプション。色(backgroundColor)は SelectFilter が描画に使う。
type StatusFilterOption = {
  value: string
  label: string
  backgroundColor?: string
}

// セルを編集したら、変更前の値を editedData に退避して赤三角マーカー(grid-edited-cell)を出す。
// 新規追加行も入力時に空→値の変更としてマーカー対象にする。
const markEdited = (
  data: SubStatusRow,
  field: keyof SubStatusRow,
  oldValue: unknown
) => {
  data.edited = true
  if (!data.editedData) {
    data.editedData = { [field]: oldValue }
  } else if (!Object.prototype.hasOwnProperty.call(data.editedData, field)) {
    data.editedData[field] = oldValue
  }
}

// 使用中セル: true のときだけ〇を出す(TicketTypes 画面と同じ表現)。
const IsUsedCellRenderer = (params: ICellRendererParams<SubStatusRow>) =>
  params.value ? '〇' : ''

// 使用中の選択式フィルタ用オプション。〇/空の2値なのでテキストフィルタでは絞り込めないため、
// 「使用中の行だけ見たい/使っていないものだけ整理したい」に応えられるよう選択式にする。
type IsUsedFilterOption = {
  value: string
  label: string
}

const isUsedFilterOption = (isUsed: boolean): IsUsedFilterOption => ({
  value: String(isUsed),
  label: intl.formatMessage({ id: isUsed ? 'yes' : 'no' }),
})

// 紐づくステータスセル: 名前は入っているのにステータスが未選択のときにエラー表示する。
// (名前セルのエラー表示は NameCellRenderer 側が担当する)
const linkedStatusCellStyle = (params: CellClassParams<SubStatusRow>) => {
  const hasName = !!(params.data?.name ?? '').trim()
  const isMissing = hasName && !params.value
  return { backgroundColor: isMissing ? ERROR_CELL_BACKGROUND : '' }
}

/**
 * サブステータス用のグリッドオプションを生成
 * statusOptionsはgridOptions構築時に固定されず、useGridContext経由でcontextに反映する
 * 削除はコンテキストメニュー(行選択→右クリック)から行う
 */
export const useSubStatusGridOptions = (): GridOptions => {
  const rowNumberColDef = useRowNumber()
  // 更新履歴(監査)列。共通プロパティを再利用。既定は hide:true で、列設定から表示切替する。
  const revisionColDef = useRevision('revision')
  const createdByColDef = useCreatedBy('createdBy')
  const createdAtColDef = useCreatedAt('createdAt')
  const updatedByColDef = useUpdatedBy('updatedBy')
  const updatedAtColDef = useUpdatedAt('updatedAt')
  const columnDefs: (ColDef | ColGroupDef)[] = [
    {
      // ドラッグハンドル列。プリセット・カスタムともに並べ替え可能。
      // 幅は TicketTypes 画面などと揃える。フィルタは不要。
      ...dragAndDrop,
      width: 30,
      maxWidth: 30,
      filter: false,
      floatingFilter: false,
    },
    {
      // 行番号列。フィルタは不要。
      ...rowNumberColDef,
      headerName: '#',
      filter: false,
      floatingFilter: false,
    },
    {
      // 名前・紐づくステータスは「基本情報」グループにまとめる。
      // 更新履歴が2段ヘッダーの列グループなので、こちらもグループ化してヘッダー段数(フィルタ行の高さ)を揃える。
      headerName: intl.formatMessage({ id: 'subStatus.basicInfo' }),
      children: [
        {
          headerName: intl.formatMessage({ id: 'subStatus.name' }),
          field: 'name',
          width: 280,
          editable: true,
          cellRenderer: NameCellRenderer,
          valueSetter: (params: ValueSetterParams<SubStatusRow>) => {
            if (params.data) {
              markEdited(params.data, 'name', params.data.name)
              params.data.name = params.newValue
              return true
            }
            return false
          },
          sortable: true,
          filter: ClientSideTextFilter,
          floatingFilter: true,
          resizable: true,
        },
        {
          headerName: intl.formatMessage({ id: 'subStatus.linkedStatus' }),
          field: 'linkedStatus',
          width: 200,
          editable: true,
          cellStyle: linkedStatusCellStyle,
          valueSetter: (params: ValueSetterParams<SubStatusRow>) => {
            if (params.data) {
              markEdited(params.data, 'linkedStatus', params.data.linkedStatus)
              params.data.linkedStatus = params.newValue
              return true
            }
            return false
          },
          cellRenderer: LinkedStatusCellRenderer,
          cellEditor: LinkedStatusCellEditor,
          // 編集中のEnter確定はStatusComboBox(Mousetrap)側で行うため、
          // ag-Grid既定のEnter(値未確定のまま編集終了して下へ移動)を抑止して二重処理を防ぐ
          suppressKeyboardEvent: params =>
            !!params.editing && params.event.key === 'Enter',
          sortable: true,
          // 紐づくステータスはプロジェクト計画のステータス列と同じ選択式フィルタにする。
          // オプションはステータスの色(getOptionStyles)付きで表示する。
          filter: ClientSideSelectFilter,
          filterParams: {
            valueGetter: (params: {
              data?: SubStatusRow
              node?: { data?: SubStatusRow }
            }): StatusFilterOption | undefined => {
              const status =
                params.data?.linkedStatus ?? params.node?.data?.linkedStatus
              if (!status) return undefined
              return {
                value: status,
                label: toLabel(status),
                // プロジェクト計画のステータス列と同じ色に合わせる。
                backgroundColor: getWbsItemStatusColorCode(status),
              }
            },
            getValue: (o?: StatusFilterOption) => o?.value ?? '',
            getLabel: (o?: StatusFilterOption) => o?.label ?? '',
          },
          floatingFilter: true,
          resizable: true,
        },
        {
          // 使用中(WBSアイテムに設定済み)かどうか。使用中の行は削除できない。
          headerName: intl.formatMessage({ id: 'subStatus.isUsed' }),
          field: 'isUsed',
          width: 100,
          editable: false,
          cellRenderer: IsUsedCellRenderer,
          cellStyle: { textAlign: 'center' },
          sortable: true,
          filter: ClientSideSelectFilter,
          filterParams: {
            valueGetter: (params: {
              data?: SubStatusRow
              node?: { data?: SubStatusRow }
            }): IsUsedFilterOption =>
              isUsedFilterOption(
                !!(params.data?.isUsed ?? params.node?.data?.isUsed)
              ),
            getValue: (o?: IsUsedFilterOption) => o?.value ?? '',
            getLabel: (o?: IsUsedFilterOption) => o?.label ?? '',
          },
          floatingFilter: true,
          resizable: true,
        },
      ],
    },
    {
      // 更新履歴列(更新回数/登録者/登録日/更新者/更新日)。TicketTypes 画面と同じ構成。
      headerName: intl.formatMessage({ id: 'changeLog' }),
      children: [
        revisionColDef,
        createdByColDef,
        createdAtColDef,
        updatedByColDef,
        updatedAtColDef,
      ],
    },
  ]

  return {
    columnDefs,
    columnTypes: columnTypes(),
    context: { statusOptions: [], duplicateNames: new Set<string>() },
    defaultColGroupDef: { suppressHeaderContextMenu: true },
    defaultColDef: {
      cellDataType: false,
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      cellEditor: 'textEditor',
      singleClickEdit: false,
      // 変更した既存行のセル右上に赤三角マーカーを出す(プロジェクト計画等と同じ)。
      cellClassRules: DEFAULT_CELL_CLASS_RULES,
    },
    cellSelection: true,
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: true,
    },
    // ドラッグ&ドロップによる並べ替え(TicketTypes 画面と同じ非管理方式)。
    // 並び順の確定は index.tsx の onRowDragEnd で行う。
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: true,
    getRowId: params => params.data.uuid,
    allowContextMenuWithControlKey: true,
    preventDefaultOnContextMenu: true,
    onRowDoubleClicked: () => false,
  }
}
